import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';

interface Category {
  id: string;
  nameHu: string;
  nameEn: string;
  descriptionHu: string;
  descriptionEn: string;
  exampleCar: string;
  order: number;
  active: boolean;
}

const CategoryManager: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<Partial<Category>>({
    nameHu: '',
    nameEn: '',
    descriptionHu: '',
    descriptionEn: '',
    exampleCar: '',
    order: 0,
    active: true
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const q = query(collection(db, 'vehicleCategories'), orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Category[];
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Hiba a kategóriák betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        // Update
        await updateDoc(doc(db, 'vehicleCategories', editingId), {
          ...formData,
          updatedAt: new Date()
        });
        toast.success('Kategória frissítve');
      } else {
        // Create
        await addDoc(collection(db, 'vehicleCategories'), {
          ...formData,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        toast.success('Kategória létrehozva');
      }
      
      setFormData({
        nameHu: '',
        nameEn: '',
        descriptionHu: '',
        descriptionEn: '',
        exampleCar: '',
        order: 0,
        active: true
      });
      setEditingId(null);
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Hiba a mentés során');
    }
  };

  const handleEdit = (category: Category) => {
    setFormData(category);
    setEditingId(category.id);
  };

  const handleDeleteClick = (category: Category) => {
    setDeletingCategory(category);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingCategory) return;
    
    try {
      await deleteDoc(doc(db, 'vehicleCategories', deletingCategory.id));
      toast.success('Kategória törölve');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Hiba a törlés során');
    } finally {
      setDeletingCategory(null);
    }
  };

  const toggleActive = async (id: string, currentActive: boolean) => {
    try {
      await updateDoc(doc(db, 'vehicleCategories', id), {
        active: !currentActive,
        updatedAt: new Date()
      });
      toast.success(currentActive ? 'Kategória inaktiválva' : 'Kategória aktiválva');
      fetchCategories();
    } catch (error) {
      console.error('Error toggling category:', error);
      toast.error('Hiba a művelet során');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Betöltés...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Form */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {editingId ? 'Kategória szerkesztése' : 'Új kategória'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Név (Magyar) *
              </label>
              <input
                type="text"
                required
                value={formData.nameHu}
                onChange={(e) => setFormData({ ...formData, nameHu: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Név (Angol) *
              </label>
              <input
                type="text"
                required
                value={formData.nameEn}
                onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Leírás (Magyar) *
              </label>
              <textarea
                required
                rows={3}
                value={formData.descriptionHu}
                onChange={(e) => setFormData({ ...formData, descriptionHu: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Leírás (Angol) *
              </label>
              <textarea
                required
                rows={3}
                value={formData.descriptionEn}
                onChange={(e) => setFormData({ ...formData, descriptionEn: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Példa autó
              </label>
              <input
                type="text"
                value={formData.exampleCar}
                onChange={(e) => setFormData({ ...formData, exampleCar: e.target.value })}
                placeholder="pl. BMW 530e or similar"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sorrend *
              </label>
              <input
                type="number"
                required
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
            >
              {editingId ? 'Frissítés' : 'Létrehozás'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setFormData({
                    nameHu: '',
                    nameEn: '',
                    descriptionHu: '',
                    descriptionEn: '',
                    exampleCar: '',
                    order: 0,
                    active: true
                  });
                }}
                className="bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-white px-4 py-2 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Mégse
              </button>
            )}
          </div>
        </form>
      </div>

      {/* List */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Sorrend
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Név (HU / EN)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Példa autó
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Státusz
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Műveletek
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {categories.map((category) => (
              <tr key={category.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {category.order}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                  <div>{category.nameHu}</div>
                  <div className="text-gray-500 dark:text-gray-400">{category.nameEn}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {category.exampleCar}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => toggleActive(category.id, category.active)}
                    className={`px-2 py-1 text-xs rounded-full ${
                      category.active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {category.active ? 'Aktív' : 'Inaktív'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleEdit(category)}
                    className="text-primary-600 hover:text-primary-900 dark:text-primary-400 mr-3"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(category)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={!!deletingCategory}
        onClose={() => setDeletingCategory(null)}
        onConfirm={handleDeleteConfirm}
        title="Kategória törlése"
        deleteMessage={`Biztosan törölni szeretnéd a(z) "${deletingCategory?.nameHu}" kategóriát?`}
        deleteWarning="Ez a művelet nem vonható vissza. A kategória véglegesen törlődik."
        danger={true}
      />
    </div>
  );
};

export default CategoryManager;
